/**
 * Student Personal Documents Panel
 * Read-only listing of the documents the STUDENT uploaded via their portal
 * (passport, CV, academic transcript, etc.). Visible to all admin/employee
 * users + agents (whose API authZ scopes to their own students).
 *
 * Backend: GET /api/admin/student/{student_id}/documents
 * Download: GET /api/student/documents/{doc_id}/download
 */
import { useEffect, useState, useCallback } from 'react';
import { FileText, Download, Loader2, Eye, Calendar, Inbox, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api, { downloadAuthenticatedFile, openAuthenticatedFile } from '@/lib/api';

const DOC_TYPE_LABELS = {
  passport: 'Passport',
  cv: 'CV / Resume',
  academic_transcript: 'Academic Transcript',
  transcript: 'Transcript',
  english_test: 'English Test Result',
  financial_proof: 'Financial Proof',
  photo: 'Photo',
  sop: 'Statement of Purpose',
  reference_letter: 'Reference Letter',
  degree_certificate: 'Degree Certificate',
  qualification: 'Academic Qualification',
  other: 'Other',
};

const STATUS_COLORS = {
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-rose-100 text-rose-700',
  pending: 'bg-amber-100 text-amber-700',
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function StudentPersonalDocsPanel({ studentId, studentName }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchDocs = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/student/${studentId}/documents`);
      setDocs(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not load student documents';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleView = async (doc) => {
    setBusyId(`${doc.id}-view`);
    try {
      const url = `${API_URL}/api/student/documents/${doc.id}/download`;
      await openAuthenticatedFile(url, doc.original_filename || doc.document_name);
    } catch (e) {
      // Fallback for legacy URL-only records
      if (doc.file_url && /^https?:/i.test(doc.file_url)) {
        window.open(doc.file_url, '_blank');
      } else {
        toast.error(e?.message || 'Document is no longer available');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (doc) => {
    setBusyId(`${doc.id}-dl`);
    try {
      const apiCall = api.get(`/student/documents/${doc.id}/download`, { responseType: 'blob' });
      await downloadAuthenticatedFile(apiCall, doc.original_filename || `${doc.document_name}.bin`);
    } catch (e) {
      toast.error(e?.message || 'Failed to download');
    } finally {
      setBusyId(null);
    }
  };

  const handleReview = async (doc, nextStatus) => {
    setBusyId(`${doc.id}-${nextStatus}`);
    try {
      await api.put(`/admin/student/documents/${doc.id}/review`, null, {
        params: { status: nextStatus },
      });
      toast.success(`Marked as ${nextStatus}`);
      // Optimistic update — flip the status in place without a refetch
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: nextStatus } : d)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update status');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="student-personal-docs-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-teal-600" />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            Student Documents{studentName ? ` — ${studentName}` : ''}
          </h3>
        </div>
        <Badge variant="outline" className="text-xs">
          {docs.length} uploaded
        </Badge>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Personal documents uploaded by the student via My Portal → My Documents (passport, CV, transcripts, English test, etc.).
        Documents start as <span className="font-semibold text-amber-700">Pending</span> until you click ✓ to <span className="font-semibold text-emerald-700">approve</span> or ✗ to <span className="font-semibold text-rose-700">reject</span> them.
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center text-center py-10 text-gray-500 dark:text-gray-400">
          <Inbox className="w-8 h-8 mb-2" />
          <p className="font-medium">No documents uploaded yet</p>
          <p className="text-xs mt-1">
            The student hasn&apos;t uploaded any personal documents yet. Ask them to log in and use My Portal → My Documents to upload their passport, CV, and academic records.
          </p>
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-300">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Document</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">Uploaded</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((doc) => (
                <tr
                  key={doc.id}
                  className="border-t border-gray-100 dark:border-slate-700 hover:bg-gray-50/40 dark:hover:bg-slate-700/30"
                  data-testid={`student-doc-row-${doc.id}`}
                >
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-800 dark:text-white truncate max-w-xs">
                      {doc.document_name || doc.original_filename || '(unnamed)'}
                    </div>
                    {doc.original_filename && doc.original_filename !== doc.document_name && (
                      <div className="text-xs text-gray-500 truncate max-w-xs">
                        {doc.original_filename}
                        {doc.size_bytes ? ` · ${(doc.size_bytes / 1024).toFixed(0)} KB` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                    {DOC_TYPE_LABELS[doc.document_type] || doc.document_type || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : '—'}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      className={`${STATUS_COLORS[doc.status] || 'bg-gray-100 text-gray-700'} capitalize`}
                      title={
                        doc.status === 'approved'
                          ? `Approved by ${doc.reviewed_by_name || 'admin'}`
                          : doc.status === 'rejected'
                          ? `Rejected by ${doc.reviewed_by_name || 'admin'}${doc.review_notes ? ` — ${doc.review_notes}` : ''}`
                          : 'Awaiting your review — click ✓ to approve or ✗ to reject'
                      }
                    >
                      {doc.status || 'pending'}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      {/* Approve / Reject — admin only, only when not already in final state */}
                      {doc.status !== 'approved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReview(doc, 'approved')}
                          disabled={busyId === `${doc.id}-approved`}
                          className="text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                          data-testid={`student-doc-approve-${doc.id}`}
                          title="Approve this document"
                        >
                          {busyId === `${doc.id}-approved` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Check className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {doc.status !== 'rejected' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReview(doc, 'rejected')}
                          disabled={busyId === `${doc.id}-rejected`}
                          className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                          data-testid={`student-doc-reject-${doc.id}`}
                          title="Reject this document"
                        >
                          {busyId === `${doc.id}-rejected` ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleView(doc)}
                        disabled={busyId === `${doc.id}-view`}
                        className="text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20"
                        data-testid={`student-doc-view-${doc.id}`}
                        title="Open in new tab"
                      >
                        {busyId === `${doc.id}-view` ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownload(doc)}
                        disabled={busyId === `${doc.id}-dl`}
                        className="text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700"
                        data-testid={`student-doc-download-${doc.id}`}
                        title="Download"
                      >
                        {busyId === `${doc.id}-dl` ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
